/**
 * Client of the ledger API.
 *
 * Operations are read-only here: the service exposes no write endpoints for
 * them, those go through the MCP entry point. The only requests that change
 * anything are the ones opening and closing a session.
 */

import type {
  Balance,
  CategoryBreakdown,
  CategoryInfo,
  Credentials,
  Debt,
  DebtDetail,
  Debts,
  DateRange,
  ListOperationsParams,
  Note,
  NotesPage,
  NoteStatusFilter,
  MandatoryBurden,
  MandatoryPayment,
  MandatoryPayments,
  MandatoryPaymentSort,
  Operation,
  OperationKind,
  OperationsPage,
  PlannedOperation,
  PlannedOperations,
  Readiness,
  Session,
  Wish,
  Wishlist,
} from './types';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '');

export async function fetchNotes(
  status: NoteStatusFilter,
  limit: number,
  offset: number,
): Promise<NotesPage> {
  const params = new URLSearchParams({ status, limit: String(limit + 1), offset: String(offset) });
  const notes = await request<Note[]>('/notes', params);
  return { notes: notes.slice(0, limit), hasMore: notes.length > limit };
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** The session is missing or has run out: the interface has to ask for a login. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

async function request<T>(
  path: string,
  params?: URLSearchParams,
  init?: RequestInit,
): Promise<T> {
  const query = params && [...params.keys()].length > 0 ? `?${params}` : '';

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}${query}`, {
      ...init,
      // The session lives in a cookie, which a cross-origin base URL would
      // otherwise leave behind.
      credentials: 'include',
      headers: { Accept: 'application/json', ...init?.headers },
    });
  } catch {
    throw new ApiError('Сервис недоступен', 0);
  }

  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return (await response.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  const init: RequestInit =
    body === undefined
      ? { method: 'POST' }
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        };

  return request<T>(path, undefined, init);
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === 'string') {
      return body.detail;
    }
  } catch {
    /* the body is not the uniform error payload — fall back to the status */
  }
  return `Запрос завершился с кодом ${response.status}`;
}

/**
 * The API returns a page without a total count, so one extra row is requested
 * to learn whether a next page exists.
 */
export async function fetchOperations({
  kind,
  category,
  since,
  until,
  limit,
  offset,
}: ListOperationsParams): Promise<OperationsPage> {
  const params = new URLSearchParams({
    since,
    until,
    limit: String(limit + 1),
    offset: String(offset),
  });
  if (kind) {
    params.set('kind', kind);
  }
  if (category) {
    params.set('category', category);
  }

  const operations = await request<Operation[]>('/operations', params);
  return { operations: operations.slice(0, limit), hasMore: operations.length > limit };
}

export function fetchOperation(id: string): Promise<Operation> {
  return request<Operation>(`/operations/${id}`);
}

/**
 * The split is asked for separately rather than added up from the page on
 * screen: it covers every operation in the selected dates, and a page is only
 * a handful of rows.
 */
export function fetchCategoryBreakdown(
  kind: OperationKind,
  range: DateRange,
): Promise<CategoryBreakdown> {
  return request<CategoryBreakdown>(
    '/operations/categories',
    new URLSearchParams({ kind, since: range.since, until: range.until }),
  );
}

/** Active one-off plans, nearest due date first. */
export async function fetchPlannedOperations(limit: number): Promise<PlannedOperations> {
  const params = new URLSearchParams({ limit: String(limit + 1) });
  const operations = await request<PlannedOperation[]>('/planned-operations', params);
  return {
    plannedOperations: operations.slice(0, limit),
    hasMore: operations.length > limit,
  };
}

/** Full, freshly fetched one-off plan shown in the details modal. */
export function fetchPlannedOperation(id: string): Promise<PlannedOperation> {
  return request<PlannedOperation>(`/planned-operations/${id}`);
}

/**
 * Every category the ledger knows, by name. Asked for whole and rarely: an
 * operation carries only the name of its category, so this is what turns that
 * name into something to show.
 */
export function fetchCategories(): Promise<CategoryInfo[]> {
  return request<CategoryInfo[]>('/categories');
}

/**
 * The wishlist is short by nature, so it is shown in one go rather than paged;
 * one extra row tells the panel whether the tail was cut off.
 */
export async function fetchWishes(limit: number): Promise<Wishlist> {
  const params = new URLSearchParams({ limit: String(limit + 1) });

  const wishes = await request<Wish[]>('/wishes', params);
  return { wishes: wishes.slice(0, limit), hasMore: wishes.length > limit };
}

/** Full, freshly fetched wish shown in the details drawer. */
export function fetchWish(id: string): Promise<Wish> {
  return request<Wish>(`/wishes/${id}`);
}

/** Short like the wishlist, so it is shown in one go; one extra row tells the
 * panel whether the tail was cut off. */
export async function fetchDebts(limit: number): Promise<Debts> {
  const params = new URLSearchParams({ limit: String(limit + 1) });

  const debts = await request<Debt[]>('/debts', params);
  return { debts: debts.slice(0, limit), hasMore: debts.length > limit };
}

/** The debt with its payment schedule — everything the details modal shows. */
export function fetchDebt(id: string): Promise<DebtDetail> {
  return request<DebtDetail>(`/debts/${id}`);
}

/** One extra row tells the panel whether the selected order was cut off. */
export async function fetchMandatoryPayments(
  limit: number,
  sort: MandatoryPaymentSort,
): Promise<MandatoryPayments> {
  const params = new URLSearchParams({ limit: String(limit + 1), sort });

  const payments = await request<MandatoryPayment[]>('/mandatory-payments', params);
  return { payments: payments.slice(0, limit), hasMore: payments.length > limit };
}

/**
 * The total is asked for separately rather than added up on screen: it covers
 * every obligation, including any the list above was cut off before.
 */
export function fetchMandatoryBurden(): Promise<MandatoryBurden> {
  return request<MandatoryBurden>('/mandatory-payments/burden');
}

export function fetchBalance(): Promise<Balance> {
  return request<Balance>('/operations/balance');
}

/**
 * The session never rejects either: being logged out is an answer, not a failure,
 * and it is the one that puts the login form on screen.
 */
export async function fetchSession(): Promise<Session> {
  try {
    return await request<Session>('/auth/session');
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      return { login: null, required: true };
    }
    throw error;
  }
}

export function logIn(credentials: Credentials): Promise<Session> {
  return post<Session>('/auth/login', credentials);
}

export function logOut(): Promise<Session> {
  return post<Session>('/auth/logout');
}

/**
 * Readiness never rejects: an unreachable service or a 503 is itself the
 * answer the status indicator needs.
 */
export async function fetchReadiness(): Promise<Readiness> {
  try {
    const response = await fetch(`${BASE_URL}/health/ready`, {
      headers: { Accept: 'application/json' },
    });
    if (response.status === 503) {
      return (await response.json()) as Readiness;
    }
    if (!response.ok) {
      return { status: 'degraded', database: 'unknown' };
    }
    return (await response.json()) as Readiness;
  } catch {
    return { status: 'unreachable', database: 'unknown' };
  }
}
